import type {
  OrganizationId,
  ResponseId,
  SurveyId,
  UserId,
} from "@/backend/db/drizzle/types/idTypes";
import type { RecruitmentId } from "@/components/outreach/panel/types";
import { notNull } from "@/utils/helpers";

// Closed set on purpose: these ride on every log, span, and Sentry event.
// Shared by the server store and the browser scope, so it must stay browser-safe.
export type ContextAttributes = Partial<{
  studyId: SurveyId;
  organizationId: OrganizationId;
  userId: UserId;
  responseId: ResponseId;
  recruitmentId: RecruitmentId;
}>;

export const attributeKeys = [
  ["studyId", "study_id"],
  ["organizationId", "organization_id"],
  ["userId", "user_id"],
  ["responseId", "response_id"],
  ["recruitmentId", "recruitment_id"],
] as const;

export type AttributeName = (typeof attributeKeys)[number][1];

// The snake_case form the sinks see: span attributes, log fields, Sentry tags.
export type AttributeRecord = Partial<Record<AttributeName, string>>;

export const toAttributeRecord = (
  attributes: ContextAttributes
): AttributeRecord =>
  Object.fromEntries(
    attributeKeys.flatMap(([key, name]) => {
      const value = attributes[key];
      return notNull(value) ? [[name, value]] : [];
    })
  );

// Missing values leave existing attributes intact.
export const mergeAttributes = (
  current: ContextAttributes,
  incoming: ContextAttributes
): ContextAttributes =>
  Object.fromEntries(
    attributeKeys.flatMap(([key]) => {
      const value = incoming[key] ?? current[key];
      return notNull(value) ? [[key, value]] : [];
    })
  );
